/**
 * Portal Cleanup Utility
 * 
 * Prevents "removeChild" errors when navigating while portals are open.
 * This catches and suppresses portal-related removeChild errors that occur
 * during navigation or component unmounting.
 */

let originalRemoveChild: typeof Node.prototype.removeChild

export function initPortalCleanup() {
  if (typeof window === 'undefined') return
  
  // Store original removeChild
  originalRemoveChild = Node.prototype.removeChild

  // Wrap removeChild to catch portal errors
  Node.prototype.removeChild = function<T extends Node>(child: T): T {
    try {
      return originalRemoveChild.call(this, child) as T
    } catch (error: any) {
      // Only suppress the specific removeChild error from portals
      if (
        error?.name === 'NotFoundError' &&
        error?.message?.includes('removeChild') &&
        error?.message?.includes('not a child')
      ) {
        // This is a portal unmounting race condition - safe to ignore
        console.warn('[Portal Cleanup] Suppressed portal removeChild error during navigation')
        return child
      }
      // Re-throw other errors
      throw error
    }
  }

  console.info('[Portal Cleanup] Initialized portal error handler')
}

export function cleanupPortalCleanup() {
  if (typeof window === 'undefined' || !originalRemoveChild) return
  
  // Restore original removeChild
  Node.prototype.removeChild = originalRemoveChild
  console.info('[Portal Cleanup] Restored original removeChild')
}
